import { cn, formatCompactNumber } from '@/lib/utils'
import type { ContextSection } from '@/types/contextObservability'
import { SECTION_COLORS } from '@/types/contextObservability'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface BudgetBarProps {
  sections: ContextSection[]
  contextWindowSize: number
  summarizeThresholdPct: number
  className?: string
}

export function BudgetBar({
  sections,
  contextWindowSize,
  summarizeThresholdPct,
  className,
}: BudgetBarProps) {
  const totalUsed = sections.reduce((sum, s) => sum + s.tokens, 0)
  const usedPct = contextWindowSize > 0 ? (totalUsed / contextWindowSize) * 100 : 0

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium">
          {formatCompactNumber(totalUsed)} / {formatCompactNumber(contextWindowSize)} tokens
        </span>
        <span className="text-xs text-muted-foreground">
          {usedPct.toFixed(1)}%
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {sections.filter(s => s.tokens > 0).map((section) => (
          <span key={section.key} className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: SECTION_COLORS[section.key] ?? section.color }}
            />
            <span>{section.label}</span>
            <span className="font-medium text-foreground">
              {formatCompactNumber(section.tokens)}
            </span>
          </span>
        ))}
      </div>

      <div className="relative">
        <TooltipProvider>
          <div className="flex h-6 w-full overflow-hidden rounded-md bg-muted">
            {sections.filter(s => s.tokens > 0).map((section) => {
              const pct = contextWindowSize > 0
                ? (section.tokens / contextWindowSize) * 100
                : 0
              if (pct < 0.1) return null
              return (
                <Tooltip key={section.key}>
                  <TooltipTrigger asChild>
                    <div
                      className="h-full transition-all hover:opacity-80"
                      style={{
                        flexBasis: `${pct}%`,
                        backgroundColor: SECTION_COLORS[section.key] ?? section.color,
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <div className="font-medium">{section.label}</div>
                      <div>{section.tokens.toLocaleString()} tokens ({pct.toFixed(1)}%)</div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        </TooltipProvider>

        {summarizeThresholdPct > 0 && summarizeThresholdPct < 100 && (
          <div
            className="absolute top-0 h-full border-l-2 border-dashed border-foreground/40"
            style={{ left: `${summarizeThresholdPct}%` }}
          >
            <span className="absolute top-full mt-1 -translate-x-1/2 text-[10px] text-muted-foreground whitespace-nowrap">
              Summarize at {summarizeThresholdPct}%
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
